import React from "react";
import useLanguage from "../../hooks/useLanguage";
import {
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/DeleteOutline";
import ContentCutIcon from "@mui/icons-material/ContentCut";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import ContentPasteIcon from "@mui/icons-material/ContentPaste";
import UndoIcon from "@mui/icons-material/Undo";
import RedoIcon from "@mui/icons-material/Redo";
import SelectAllIcon from "@mui/icons-material/SelectAll";
import LinkIcon from "@mui/icons-material/Link";

// Custom right-click menu for the editor. Media-aware: shows
// "Edit"/"Delete"/"Copy URL" + media-specific Copy/Cut when the target is an
// <img>/<video>. Standard edit commands always present (operate on text
// selection via document.execCommand).
const safeExec = (cmd) => {
  try {
    document.execCommand(cmd);
  } catch (e) {
    // execCommand is deprecated and sometimes throws (e.g. paste without a
    // user-gesture). Swallow — there's nothing useful to do.
  }
};

// Copy a media URL to clipboard. For images, also tries to copy the actual
// image bytes via the async Clipboard API so the user can paste into other
// apps. Falls back to URL-as-text on any failure.
const copyMediaToClipboard = async ({ type, src }) => {
  if (!src) return;
  if (type === "img") {
    try {
      const baseUrl = process.env.REACT_APP_API_URL || "";
      const token = localStorage.getItem("x-token");
      const res = await fetch(
        `${baseUrl}/api/media/proxy?url=${encodeURIComponent(src)}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!res.ok) throw new Error(`proxy ${res.status}`);
      const blob = await res.blob();
      if (typeof ClipboardItem !== "undefined" && navigator.clipboard?.write) {
        await navigator.clipboard.write([
          new ClipboardItem({ [blob.type]: blob }),
        ]);
        return;
      }
    } catch (e) {
      // fall through to URL-as-text
    }
  }
  try {
    await navigator.clipboard.writeText(src);
  } catch (e) {
    // last-resort: ignore
  }
};

const EditorContextMenu = ({
  menu,
  onClose,
  onEditImage,
  onEditVideo,
  onDeleteMedia,
}) => {
  const [{ components: { mediaEditor } }] = useLanguage();
  const t = mediaEditor.contextMenu;
  const open = Boolean(menu);
  const type = menu?.type || "text";
  const src = menu?.src || null;
  const hasMediaItems = type === "img" || type === "video";

  const wrap = (fn) => () => {
    fn();
    onClose();
  };

  // Media copy/cut go through async clipboard; defer close until done so
  // the click context window doesn't disappear while we're still calling
  // navigator.clipboard.
  const handleCopyMedia = async () => {
    await copyMediaToClipboard({ type, src });
    onClose();
  };

  const handleCutMedia = async () => {
    await copyMediaToClipboard({ type, src });
    if (onDeleteMedia) onDeleteMedia(src, type);
    onClose();
  };

  const handleDeleteMedia = () => {
    if (onDeleteMedia) onDeleteMedia(src, type);
    onClose();
  };

  const handleCopyUrl = wrap(async () => {
    if (src) {
      try {
        await navigator.clipboard.writeText(src);
      } catch (e) {
        // ignore
      }
    }
  });

  return (
    <Menu
      open={open}
      onClose={onClose}
      anchorReference="anchorPosition"
      anchorPosition={menu ? { top: menu.y, left: menu.x } : undefined}
      // Prevent MUI from restoring focus to the contentEditable on close —
      // that triggers a scroll-into-view jump.
      autoFocus={false}
      disableAutoFocus
      disableAutoFocusItem
      disableEnforceFocus
      disableRestoreFocus
      MenuListProps={{ autoFocus: false, autoFocusItem: false }}
    >
      {type === "img" && (
        <MenuItem onClick={wrap(() => onEditImage(src))}>
          <ListItemIcon>
            <EditIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>{t.editImage}</ListItemText>
        </MenuItem>
      )}
      {type === "video" && (
        <MenuItem onClick={wrap(() => onEditVideo(src))}>
          <ListItemIcon>
            <EditIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>{t.editVideo}</ListItemText>
        </MenuItem>
      )}
      {hasMediaItems && (
        <MenuItem onClick={handleDeleteMedia}>
          <ListItemIcon>
            <DeleteIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>{type === "video" ? t.deleteVideo : t.deleteImage}</ListItemText>
        </MenuItem>
      )}
      {hasMediaItems && (
        <MenuItem onClick={handleCopyUrl}>
          <ListItemIcon>
            <LinkIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>{t.copyUrl}</ListItemText>
        </MenuItem>
      )}
      {hasMediaItems && <Divider />}

      <MenuItem
        onClick={hasMediaItems ? handleCutMedia : wrap(() => safeExec("cut"))}
      >
        <ListItemIcon>
          <ContentCutIcon fontSize="small" />
        </ListItemIcon>
        <ListItemText>{t.cut}</ListItemText>
      </MenuItem>
      <MenuItem
        onClick={hasMediaItems ? handleCopyMedia : wrap(() => safeExec("copy"))}
      >
        <ListItemIcon>
          <ContentCopyIcon fontSize="small" />
        </ListItemIcon>
        <ListItemText>{t.copy}</ListItemText>
      </MenuItem>
      <MenuItem onClick={wrap(() => safeExec("paste"))}>
        <ListItemIcon>
          <ContentPasteIcon fontSize="small" />
        </ListItemIcon>
        <ListItemText>{t.paste}</ListItemText>
      </MenuItem>

      <Divider />

      <MenuItem onClick={wrap(() => safeExec("undo"))}>
        <ListItemIcon>
          <UndoIcon fontSize="small" />
        </ListItemIcon>
        <ListItemText>{t.undo}</ListItemText>
      </MenuItem>
      <MenuItem onClick={wrap(() => safeExec("redo"))}>
        <ListItemIcon>
          <RedoIcon fontSize="small" />
        </ListItemIcon>
        <ListItemText>{t.redo}</ListItemText>
      </MenuItem>

      <Divider />

      <MenuItem onClick={wrap(() => safeExec("selectAll"))}>
        <ListItemIcon>
          <SelectAllIcon fontSize="small" />
        </ListItemIcon>
        <ListItemText>{t.selectAll}</ListItemText>
      </MenuItem>
    </Menu>
  );
};

export default EditorContextMenu;
